import React, { useEffect, useState } from 'react';
import { Form, Row, Col, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import {
  ResourceColumn,
  ResourceListFilterHelpers,
  ResourceListPage,
} from '../../components/resource/ResourceListPage';
import { ContagemEstoqueDiaria } from '../../types/estoque';
import {
  catalogoProdutosService,
  contagensEstoqueService,
  armazensService,
} from '../../services';
import { ListParams } from '../../services/crudFactory';

const formatProduto = (item: ContagemEstoqueDiaria) => {
  const codigo = item.produto_codigo ?? String(item.produto);
  if (item.produto_descricao) {
    return `${codigo} - ${item.produto_descricao}`;
  }
  return codigo;
};

const columns: ResourceColumn<ContagemEstoqueDiaria>[] = [
  { header: 'Produto', render: formatProduto },
  { header: 'Data', accessor: 'data_contagem' },
  { header: 'Quantidade', accessor: 'quantidade_contada' },
  { header: 'Armazém', accessor: 'armazem_nome' },
  { header: 'Localização', accessor: 'localizacao' },
  { header: 'Responsável', accessor: 'responsavel' },
  {
    header: 'Observações',
    accessor: 'observacoes',
    render: (item) => item.observacoes ?? '-',
  },
];

type ContagensFilters = {
  produto: string;
  armazem: string;
  data_contagem__gte: string;
  data_contagem__lte: string;
};

const defaultContagensFilters: ContagensFilters = {
  produto: '',
  armazem: '',
  data_contagem__gte: '',
  data_contagem__lte: '',
};

type SelectOption = {
  value: number;
  label: string;
};

type FormControlElement = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

const ContagensDiariasFilters: React.FC<{
  params: ListParams;
  setParams: ResourceListFilterHelpers['setParams'];
}> = ({ params, setParams }) => {
  const [filters, setFilters] = useState(defaultContagensFilters);
  const [produtos, setProdutos] = useState<SelectOption[]>([]);
  const [armazens, setArmazens] = useState<SelectOption[]>([]);
  const [loadingProdutos, setLoadingProdutos] = useState(true);
  const [loadingArmazens, setLoadingArmazens] = useState(true);

  useEffect(() => {
    setFilters({
      produto: params.produto ? String(params.produto) : '',
      armazem: params.armazem ? String(params.armazem) : '',
      data_contagem__gte: (params.data_contagem__gte as string) ?? '',
      data_contagem__lte: (params.data_contagem__lte as string) ?? '',
    });
  }, [
    params.armazem,
    params.data_contagem__gte,
    params.data_contagem__lte,
    params.produto,
  ]);

  useEffect(() => {
    let isMounted = true;

    catalogoProdutosService
      .list({ page_size: 200 })
      .then((response) => {
        if (!isMounted) return;
        setProdutos(
          response.results.map((produto) => ({
            value: produto.id,
            label: `${produto.codigo ?? ''} ${produto.descricao ?? ''}`.trim() || String(produto.id),
          })),
        );
      })
      .catch(() => {
        if (isMounted) {
          setProdutos([]);
        }
      })
      .finally(() => {
        if (isMounted) {
          setLoadingProdutos(false);
        }
      });

    armazensService
      .list({ page_size: 200 })
      .then((response) => {
        if (!isMounted) return;
        setArmazens(
          response.results.map((armazem) => ({
            value: armazem.id,
            label: armazem.nome ?? `Armazém ${armazem.id}`,
          })),
        );
      })
      .catch(() => {
        if (isMounted) {
          setArmazens([]);
        }
      })
      .finally(() => {
        if (isMounted) {
          setLoadingArmazens(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const handleChange =
    (field: keyof ContagensFilters) =>
    (event: React.ChangeEvent<FormControlElement>) =>
      setFilters((prev) => ({ ...prev, [field]: event.target.value }));

  const applyFilters = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setParams((prev) => ({
      ...prev,
      page: 1,
      produto: filters.produto || undefined,
      armazem: filters.armazem || undefined,
      data_contagem__gte: filters.data_contagem__gte || undefined,
      data_contagem__lte: filters.data_contagem__lte || undefined,
    }));
  };

  const clearFilters = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setFilters(defaultContagensFilters);
    setParams((prev) => ({
      ...prev,
      page: 1,
      produto: undefined,
      armazem: undefined,
      data_contagem__gte: undefined,
      data_contagem__lte: undefined,
    }));
  };

  return (
    <div className="border rounded-3 p-3 bg-white">
      <Form onSubmit={applyFilters}>
        <Row className="g-3 align-items-end">
          <Col xs={12} md={3}>
            <Form.Group controlId="filterProduto">
              <Form.Label>Produto</Form.Label>
              <Form.Select
                value={filters.produto}
                onChange={handleChange('produto')}
                disabled={loadingProdutos}
              >
                <option value="">Todos</option>
                {produtos.map((produto) => (
                  <option key={produto.value} value={produto.value}>
                    {produto.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col xs={12} md={3}>
            <Form.Group controlId="filterArmazem">
              <Form.Label>Armazém</Form.Label>
              <Form.Select
                value={filters.armazem}
                onChange={handleChange('armazem')}
                disabled={loadingArmazens}
              >
                <option value="">Todos</option>
                {armazens.map((armazem) => (
                  <option key={armazem.value} value={armazem.value}>
                    {armazem.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col xs={12} md={2}>
            <Form.Group controlId="filterDataInicio">
              <Form.Label>Data mínima</Form.Label>
              <Form.Control
                type="date"
                value={filters.data_contagem__gte}
                onChange={handleChange('data_contagem__gte')}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={2}>
            <Form.Group controlId="filterDataFim">
              <Form.Label>Data máxima</Form.Label>
              <Form.Control
                type="date"
                value={filters.data_contagem__lte}
                onChange={handleChange('data_contagem__lte')}
              />
            </Form.Group>
          </Col>
          <Col xs="auto">
            <div className="d-flex flex-wrap gap-2">
              <Button type="submit">Aplicar filtros</Button>
              <Button variant="outline-secondary" type="button" onClick={clearFilters}>
                Limpar
              </Button>
            </div>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export const ContagensDiariasListPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <ResourceListPage
      title="Contagens diárias de estoque"
      description="Liste, busque e atualize as contagens físicas registradas."
      columns={columns}
      service={contagensEstoqueService}
      createPath="/admin/producao/contagens-diarias/novo"
      onEdit={(record) => record.id && navigate(`/admin/producao/contagens-diarias/${record.id}`)}
      searchPlaceholder="Buscar por produto, armazém ou responsável..."
      filters={({ params, setParams }) => (
        <ContagensDiariasFilters params={params} setParams={setParams} />
      )}
    />
  );
};
